import { forwardRef, useCallback } from "react"

// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor"

// --- UI Primitives ---
import type { ButtonProps } from "@/components/tiptap-ui-primitive/button"
import { Button } from "@/components/tiptap-ui-primitive/button"

// --- Icons ---
import { TableIcon } from "@/components/tiptap-icons/table-icon"

export interface TableButtonProps extends Omit<ButtonProps, "type"> {
  /**
   * Optional text to display alongside the icon.
   */
  text?: string
}

/**
 * Simple toolbar button to insert a table into the editor.
 * Matches the look-and-feel of the other toolbar buttons and
 * uses the same tooltip pattern for the black label above on hover.
 */
export const TableButton = forwardRef<HTMLButtonElement, TableButtonProps>(
  ({ text, onClick, ...buttonProps }, ref) => {
    const { editor } = useTiptapEditor()

    const label = "Insert table"

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event)
        if (event.defaultPrevented) return
        if (!editor) return
        // Insert a small starter table; users can resize with existing handles.
        // We keep header row on to mirror scientific document usage.
        (editor as any)
          .chain()
          .focus()
          .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
          .run()
      },
      [editor, onClick]
    )

    // If editor is not ready, don't render the button.
    if (!editor) {
      return null
    }

    // Try to detect whether table insertion is currently allowed.
    const canInsert =
      typeof (editor as any).can?.().insertTable === "function"
        ? (editor as any).can().insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        : true

    return (
      <Button
        type="button"
        data-style="ghost"
        role="button"
        tabIndex={-1}
        disabled={!canInsert}
        data-disabled={!canInsert}
        aria-label={label}
        tooltip={label}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        <>
          <TableIcon className="tiptap-button-icon" />
          {text && <span className="tiptap-button-text">{text}</span>}
        </>
      </Button>
    )
  }
)

TableButton.displayName = "TableButton"

